/** @format */

import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { PrometheusAlarmTabProps } from 'src/pages/PrometheusMonitor/Alarm'
import {
  Button,
  Select,
  Option,
  Table,
  TableColumn,
  Pagination,
  Dialog,
  Message,
  Icon,
  Tag,
  Ellipsis
} from '@king-design/react'
import { useHistory } from 'react-router-dom'
import { PageProps, initPage } from 'src/utils/interface'
import { useAsyncFn } from 'react-use'
import {
  getPrometheusAlertSilences,
  deletePrometheusAlertSilence
} from 'src/api/ApiPrometheusAlarm'
import { SilenceStatusEnum } from '../constants'
import { MonitorSearch, TableSearch, getURLTableSearchParams } from 'src/components'
import dayjs from 'dayjs'

interface SearchProps extends PageProps {
  InstanceID: string
  Name: string
  States: (0 | 1 | 2 | 3)[]
}

const initSearch: SearchProps = {
  InstanceID: '',
  Name: '',
  States: [0],
  ...initPage
}
const SilenceTab: FC<PrometheusAlarmTabProps> = ({ className, instances, region }) => {
  const history = useHistory()
  const [search, setSearch] = useState<SearchProps>(getURLTableSearchParams() || initSearch)

  useEffect(() => {
    ruleFetch()
  }, [search, region])

  const [{ value: rules }, ruleFetch] = useAsyncFn(
    async (values?) => {
      const params = {
        ...search,
        ...values
      }
      const result = await getPrometheusAlertSilences(params)
      return {
        data: result?.Rules || [],
        total: result?.TotalCount || 0
      }
    },
    [search]
  )

  // 删除规则
  const [, deleteFetch] = useAsyncFn(
    async ID => {
      const result = await deletePrometheusAlertSilence({
        ID
      })
      if (result?.code === 'Success') {
        Message.success('删除成功')
        setSearch({
          ...search,
          Page: 1
        })
      } else {
        result?.message && Message.error(result?.message)
      }
    },
    [search]
  )

  return (
    <div className={className}>
      <div className="header">
        <Button
          type="primary"
          onClick={() => {
            history.push('/monitor/prometheusmonitor/alarm/slience/edit')
          }}
        >
          <Icon className="ion-plus-round" />
          新建静默规则
        </Button>
      </div>
      <TableSearch params={search} syncUrl={false}>
        <div className="search_item">
          <p className="search_item_label">关联资源</p>
          <Select
            value={search.InstanceID}
            clearable
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                InstanceID: value,
                Page: 1
              })
            }}
          >
            {instances?.map(item => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">静默状态</p>
          <Select
            value={search.States}
            multiple
            filterable
            onChangeValue={value => {
              setSearch({
                ...search,
                States: value.includes(0) ? [0] : value,
                Page: 1
              })
            }}
          >
            {Object.entries(SilenceStatusEnum).map(([key, value]) => (
              <Option
                disabled={search.States.includes(0) && parseInt(key) !== 0}
                key={key}
                value={parseInt(key)}
              >
                {value}
              </Option>
            ))}
          </Select>
        </div>
        <div className="search_item">
          <p className="search_item_label">静默规则名称</p>
          <MonitorSearch
            placeholder="请输入规则名称，支持模糊搜索"
            value={search.Name}
            onChange={value => {
              setSearch({
                ...search,
                Page: 1,
                Name: value
              })
            }}
          />
        </div>
      </TableSearch>
      <Table data={rules?.data || []} rowKey={data => data.ID} checkType="none" animation={false}>
        <TableColumn key="Name" title="静默规则名称" />
        <TableColumn
          key="BindInstances"
          title="关联资源"
          slotTemplate={([data]) => {
            return data.BindInstances?.length
              ? data.BindInstances?.map(item => {
                  return (
                    <>
                      <p>{item.InstanceName}</p>
                      <Ellipsis className="table_text" maxLines={2} hoverable>
                        {item.InstanceID}
                      </Ellipsis>
                    </>
                  )
                })
              : '-'
          }}
        ></TableColumn>
        <TableColumn
          key="SilenceStatus"
          title="静默规则状态"
          slotTemplate={([{ SilenceStatus }]) => {
            if (SilenceStatus === 1) {
              return (
                <Tag border="solid" type="success">
                  生效中
                </Tag>
              )
            } else if (SilenceStatus === 2) {
              return (
                <Tag border="solid" type="warning">
                  待生效
                </Tag>
              )
            } else if (SilenceStatus === 3) {
              return (
                <Tag border="solid" type="danger">
                  已过期
                </Tag>
              )
            }
          }}
        ></TableColumn>
        <TableColumn
          key="StartsAt"
          title="静默时间"
          slotTemplate={([{ StartsAt, EndsAt }]) => {
            return (
              dayjs(StartsAt * 1000).format('YYYY-MM-DD HH:mm:ss') +
              ' - ' +
              dayjs(EndsAt * 1000).format('YYYY-MM-DD HH:mm:ss')
            )
          }}
        ></TableColumn>
        <TableColumn
          key="Description"
          title="描述"
          slotTemplate={([{ Description }]) => {
            return Description ? (
              <Ellipsis maxLines={3} hoverable>
                {Description}
              </Ellipsis>
            ) : (
              '-'
            )
          }}
        ></TableColumn>
        <TableColumn
          key="UpdatedAt"
          title="编辑时间"
          slotTemplate={([{ UpdatedAt }]) => {
            return UpdatedAt || '-'
          }}
        ></TableColumn>
        <TableColumn
          key="operate"
          title="操作"
          width="150"
          slotTemplate={([record]) => {
            return (
              <div className="ksc-space">
                <a
                  onClick={() => {
                    history.push(
                      `/monitor/prometheusmonitor/alarm/slience/edit?slienceID=${record.ID}`
                    )
                  }}
                >
                  编辑
                </a>
                <a
                  onClick={() => {
                    history.push(
                      `/monitor/prometheusmonitor/alarm/slience/edit?copyID=${record.ID}`
                    )
                  }}
                >
                  复制
                </a>
                <a
                  onClick={() => {
                    let dialog: Dialog
                    Dialog.confirm({
                      title: record?.Name,
                      content: `是否确认删除所选抑制规则？`,
                      ref: _dialog => (dialog = _dialog),
                      okText: '确认删除',
                      ok: async () => {
                        dialog.showLoading()
                        await deleteFetch(record?.ID)
                        dialog.close()
                      }
                    })
                  }}
                >
                  删除
                </a>
              </div>
            )
          }}
        ></TableColumn>
      </Table>
      <Pagination
        value={search.Page}
        limit={search.PageSize}
        total={rules?.total || 0}
        size="small"
        onChangeValue={value => {
          setSearch(state => {
            return {
              ...state,
              Page: value
            }
          })
        }}
        onChangeLimit={limit => {
          setSearch({
            ...search,
            PageSize: limit
          })
        }}
      />
    </div>
  )
}

export default styled(SilenceTab)`
  margin-top: 16px;
`
